import { Container } from "@mui/material";
import { requester } from "App";
import clsx from "classnames";
import CustomButton from "components/buttons/CustomButton";
import Dummy from "components/Dummy";
import P from "components/P";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import VerticalFlex from "layouts/flex/VerticalFlex";
import Center from "layouts/wrapper/Center";
import { AuthContext } from "providers/AuthProvider";
import { useContext, useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { removeLocalStorage } from "shared/utils/Utils";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { useDispatch } from "react-redux";
import biometricService from "shared/BiometricService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import NiceModal from "@ebay/nice-modal-react";
import { devLog } from "utils/logger";

function MemberWithdrawal() {
    const [currentStep, setCurrentStep] = useState(1);
    const [withdrawalReason, setWithdrawalReason] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [isAgreed, setIsAgreed] = useState(false);
    
    const { userName, userId } = useContext(AuthContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const withdrawalReasons = [
        { id: "service_unsatisfied", label: "서비스가 만족스럽지 않아서", icon: fas.faThumbsDown },
        { id: "rarely_use", label: "자주 사용하지 않아서", icon: fas.faClock },
        { id: "privacy_concern", label: "개인정보 보호가 우려되어서", icon: fas.faShieldAlt },
        { id: "alternative_found", label: "더 좋은 대안을 찾아서", icon: fas.faExchangeAlt },
        { id: "temporary_break", label: "잠시 사용을 중단하고 싶어서", icon: fas.faPause },
        { id: "other", label: "기타", icon: fas.faEllipsisH }
    ];

    const handleReasonSelect = (reasonId) => {
        setWithdrawalReason(reasonId);
    };

    const handleNextStep = () => {
        if (currentStep === 1 && !withdrawalReason) {
            toast.error("탈퇴 사유를 선택해주세요.");
            return;
        }
        if (currentStep === 2 && confirmText !== "탈퇴합니다") {
            toast.error("정확히 '탈퇴합니다'를 입력해주세요.");
            return;
        }
        if (currentStep === 3 && !isAgreed) {
            toast.error("주의사항에 동의해주세요.");
            return;
        }
        
        setCurrentStep(currentStep + 1);
    };

    const handlePrevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const onWithdrawalClick = async () => {
        devLog("🔥 [탈퇴] 버튼 클릭됨");
        
        try {
            devLog("🔥 [탈퇴] 최종 확인");
            
            // 간단한 네이티브 확인 대화상자 사용
            const finalConfirm = window.confirm(
                "정말로 탈퇴하시겠습니까?\n\n모든 데이터가 삭제되며 복구할 수 없습니다.\n\n계속하시려면 '확인'을 눌러주세요."
            );

            if (!finalConfirm) {
                devLog("🔥 [탈퇴] 사용자가 취소함");
                return;
            }

            devLog("🔥 [탈퇴] 서버 요청 시작");
            
            // 실제 탈퇴 처리
            let userData = { id: userId, reason: withdrawalReason };
            
            requester.memberWithdrawal(userData, async (result) => {
                try {
                    devLog("🔥 [탈퇴] 서버 탈퇴 처리 완료:", result);
                    
                    await biometricService.clearAllAuthenticationData();
                    devLog("🔥 [탈퇴] 네이티브 인증 데이터 초기화 완료");
                    
                    removeLocalStorage("token");
                    dispatch(AuthReducer.actions.reset());
                    
                    toast.success("회원탈퇴가 완료되었습니다. 모든 인증 정보가 초기화되었습니다.", {
                        autoClose: 3000, 
                        position: toast.POSITION.BOTTOM_CENTER
                    });
                    
                    setTimeout(() => {
                        window.location.replace(`/ws/${id}/mobile/main`);
                    }, 1000);
                    
                } catch (authDataError) {
                    console.error("🔥 [탈퇴] 인증 데이터 초기화 오류:", authDataError);
                    
                    removeLocalStorage("token");
                    dispatch(AuthReducer.actions.reset());
                    
                    toast.warning("회원탈퇴는 완료되었으나 일부 기기 데이터 삭제에 실패했습니다.", {
                        autoClose: 3000, 
                        position: toast.POSITION.BOTTOM_CENTER
                    });
                    
                    setTimeout(() => {
                        window.location.replace(`/ws/${id}/mobile/main`);
                    }, 1000);
                }
            });
            
        } catch (error) {
            console.error("🔥 [탈퇴] 전체 처리 오류:", error);
            toast.error("회원탈퇴 처리 중 오류가 발생했습니다.", {
                autoClose: 3000, 
                position: toast.POSITION.BOTTOM_CENTER
            });
        }
    };

    const renderStep1 = () => (
        <div style={{ padding: "24px", background: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <FontAwesomeIcon icon={fas.faQuestionCircle} style={{ fontSize: "48px", color: "#ff6b6b", marginBottom: "16px" }} />
                <P size={24} weight={700} color="#333">탈퇴 사유를 알려주세요</P>
                <P size={14} color="#666">더 나은 서비스를 위해 사유를 선택해주세요</P>
            </div>
            
            <div style={{ display: "grid", gap: "12px" }}>
                {withdrawalReasons.map((reason) => (
                    <div
                        key={reason.id}
                        onClick={() => handleReasonSelect(reason.id)}
                        style={{
                            padding: "16px",
                            border: withdrawalReason === reason.id ? "2px solid #ff6b6b" : "1px solid #e9ecef",
                            borderRadius: "12px",
                            cursor: "pointer",
                            background: withdrawalReason === reason.id ? "#fff5f5" : "#ffffff",
                            transition: "all 0.2s ease"
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <FontAwesomeIcon icon={reason.icon} style={{ color: withdrawalReason === reason.id ? "#ff6b6b" : "#666" }} />
                            <P size={16} weight={500} color={withdrawalReason === reason.id ? "#ff6b6b" : "#333"}>
                                {reason.label}
                            </P>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div style={{ padding: "24px", background: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <FontAwesomeIcon icon={fas.faKeyboard} style={{ fontSize: "48px", color: "#ff6b6b", marginBottom: "16px" }} />
                <P size={24} weight={700} color="#333">확인을 위해 입력해주세요</P>
                <P size={14} color="#666">아래 입력창에 <span style={{ color: "#ff6b6b", fontWeight: "700" }}>'탈퇴합니다'</span>를 정확히 입력하세요</P>
            </div>
            
            <div style={{ marginBottom: "20px" }}>
                <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="탈퇴합니다"
                    style={{
                        width: "100%",
                        padding: "16px",
                        border: confirmText === "탈퇴합니다" ? "2px solid #28a745" : "1px solid #e9ecef",
                        borderRadius: "12px",
                        fontSize: "16px",
                        outline: "none",
                        background: "#ffffff"
                    }}
                />
                {confirmText && confirmText !== "탈퇴합니다" && (
                    <P size={12} color="#ff6b6b" style={{ marginTop: "8px", marginLeft: "4px" }}>
                        정확히 '탈퇴합니다'를 입력해주세요
                    </P>
                )}
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div style={{ padding: "24px", background: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <FontAwesomeIcon icon={fas.faExclamationTriangle} style={{ fontSize: "48px", color: "#ffc107", marginBottom: "16px" }} />
                <P size={24} weight={700} color="#333">주의사항을 확인하세요</P>
            </div>
            
            <div style={{ 
                background: "#fff3cd", 
                border: "1px solid #ffeaa7", 
                borderRadius: "12px", 
                padding: "20px", 
                marginBottom: "20px" 
            }}>
                <div style={{ marginBottom: "16px" }}>
                    <P size={16} weight={700} color="#856404">⚠️ 탈퇴 시 주의사항</P>
                </div>
                <div style={{ marginLeft: "16px" }}>
                    <P size={14} color="#856404" style={{ marginBottom: "8px" }}>• 모든 개인정보 및 이용기록 삭제</P>
                    <P size={14} color="#856404" style={{ marginBottom: "8px" }}>• 모든 혜택 소멸</P>
                    <P size={14} color="#856404" style={{ marginBottom: "8px" }}>• 30일간 비활성화 후 탈퇴</P>
                    <P size={14} color="#856404" style={{ marginBottom: "8px" }}>• 30일 이내 재 로그인 시 비활성화 해제</P>
                </div>
            </div>

            

            <div 
                onClick={() => setIsAgreed(!isAgreed)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px",
                    border: "1px solid #e9ecef",
                    borderRadius: "12px",
                    cursor: "pointer",
                    background: isAgreed ? "#f8f9fa" : "#ffffff"
                }}
            >
                <div style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "6px",
                    border: isAgreed ? "2px solid #ff6b6b" : "2px solid #dee2e6",
                    background: isAgreed ? "#ff6b6b" : "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    {isAgreed && <FontAwesomeIcon icon={fas.faCheck} style={{ color: "#ffffff", fontSize: "14px" }} />}
                </div>
                <P size={16} weight={600} color="#333">
                    상기 내용을 모두 확인했으며 탈퇴에 동의합니다
                </P>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div style={{ padding: "24px", background: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    background: "#ff6b6b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px auto",
                    boxShadow: "0 8px 20px rgba(255, 107, 107, 0.3)"
                }}>
                    <FontAwesomeIcon 
                        icon={fas.faExclamationTriangle} 
                        style={{ 
                            fontSize: "32px", 
                            color: "#ffffff"
                        }} 
                    />
                </div>
                <P size={24} weight={700} color="#333">
                    탈퇴 준비 완료
                </P>
                <P size={14} color="#666">
                    아래 버튼을 눌러 탈퇴를 완료하세요
                </P>
            </div>

            <button
                onClick={onWithdrawalClick}
                style={{
                    width: "100%",
                    padding: "16px",
                    background: "linear-gradient(135deg, #ff6b6b, #ff8e8e)",
                    border: "none",
                    borderRadius: "12px",
                    color: "#ffffff",
                    fontSize: "18px",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: "0 4px 12px rgba(255, 107, 107, 0.3)"
                }}
                onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 6px 16px rgba(255, 107, 107, 0.4)";
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 4px 12px rgba(255, 107, 107, 0.3)";
                }}
            >
                <FontAwesomeIcon icon={fas.faSignOutAlt} style={{ marginRight: "8px" }} />
                최종 탈퇴하기
            </button>
        </div>
    );

    return (
        <div style={{ minHeight: "100vh", background: "#f8f9fa", padding: "20px" }}>
            <Dummy height={"60px"} />
            <div style={{ maxWidth: "480px", margin: "0 auto" }}>
                {/* 진행 단계 표시 */}
                <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    marginBottom: "32px",
                    padding: "20px",
                    background: "#ffffff",
                    borderRadius: "16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}>
                    {[1, 2, 3, 4].map((step) => (
                        <div key={step} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <div style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                background: currentStep >= step ? "#ff6b6b" : "#e9ecef",
                                color: "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "700",
                                marginBottom: "8px"
                            }}>
                                {step}
                            </div>
                            <P size={12} color={currentStep >= step ? "#ff6b6b" : "#999"}>
                                {step === 1 && "사유선택"}
                                {step === 2 && "텍스트확인"}
                                {step === 3 && "주의사항"}
                                {step === 4 && "최종확인"}
                            </P>
                        </div>
                    ))}
                </div>

                {/* 단계별 컨텐츠 */}
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
                {currentStep === 4 && renderStep4()}

                {/* 네비게이션 버튼 */}
                {currentStep < 4 && (
                    <div style={{ 
                        display: "flex", 
                        gap: "12px", 
                        marginTop: "24px",
                        padding: "20px",
                        background: "#ffffff",
                        borderRadius: "16px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                    }}>
                        {currentStep > 1 && (
                            <button
                                onClick={handlePrevStep}
                                style={{
                                    flex: 1,
                                    padding: "14px",
                                    background: "#f8f9fa",
                                    border: "1px solid #e9ecef",
                                    borderRadius: "12px",
                                    color: "#495057",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >
                                이전
                            </button>
                        )}
                        <button
                            onClick={handleNextStep}
                            style={{
                                flex: 1,
                                padding: "14px",
                                background: "linear-gradient(135deg, #ff6b6b, #ff8e8e)",
                                border: "none",
                                borderRadius: "12px",
                                color: "#ffffff",
                                fontSize: "16px",
                                fontWeight: "600",
                                cursor: "pointer"
                            }}
                        >
                            다음
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MemberWithdrawal;